import { useCallback } from 'react';
import type { RefObject } from 'react';
import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type {
	ExtensionConfiguration,
	SelectionExtensionPlugin,
	ToolbarExtensionConfiguration,
} from '@atlaskit/editor-plugin-selection-extension';
import TemplateIcon from '@atlaskit/icon-lab/core/template';

import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import { expVal } from '@confluence/feature-experiments';

import { messages } from '../messages';
import { CreateSnippetModal } from '../modals/CreateSnippetModal';

export type UseCreateSnippetExtensionProps = {
	editorAPIRef: RefObject<PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined>;
};

export const useCreateSnippetExtension = ({
	editorAPIRef,
}: UseCreateSnippetExtensionProps): ExtensionConfiguration[] => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showModal } = useDialogs();

	const fireCreateSnippetButtonClickedAnalytics = useCallback(
		(selectionLength: number) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'confluenceHighlightActionsMenu',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'createSnippet',
					type: 'ui',
					attributes: {
						selectionLength,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const handleCreateSnippet = useCallback(() => {
		if (!editorAPIRef?.current) {
			return;
		}

		const actions = editorAPIRef?.current?.selectionExtension?.actions;
		const document = actions?.getDocumentFromSelection();
		const adf = document?.selectedNodeAdf;
		const adfLength = JSON.stringify(adf)?.length;

		if (!adf || !adfLength) {
			return;
		}

		fireCreateSnippetButtonClickedAnalytics(adfLength);
		showModal(CreateSnippetModal, { adf });
	}, [editorAPIRef, fireCreateSnippetButtonClickedAnalytics, showModal]);

	if (!expVal('cc_snippets', 'isEnabled', false)) {
		return [];
	}

	const toolbarConfiguration: ToolbarExtensionConfiguration = {
		getMenuItems: () => {
			return [
				{
					label: formatMessage(messages.createTemplate),
					lozenge: {
						label: formatMessage(messages.createTemplateLozenge),
					},
					icon: TemplateIcon,
					onClick: () => {
						void handleCreateSnippet();
					},
				},
			];
		},
	};

	return [
		{
			key: 'create-snippet',
			source: 'first-party',
			inlineToolbar: toolbarConfiguration,
		},
	];
};
